import { API_BASE_URL } from './client.js';

/**
 * The user's own account, as Better Auth owns it.
 *
 * Kept apart from `hooks.ts` for the same reason `signIn` is kept out of
 * `ApiClient`: Better Auth owns `/api/auth/*` and answers in its own shape, not
 * the API's error envelope. Treating these as `ApiClient` calls would report
 * `unknown_error` for "wrong password", which is the one message that has to be
 * right on a page full of password prompts.
 *
 * Everything here is **user-scoped**, not company-scoped. None of it changes
 * when the company switcher moves, which is why Min konto sits in the header
 * cluster rather than under Indstillinger.
 */

/** What Better Auth says about the current session. */
export interface SessionUser {
  readonly email: string;
  readonly name: string;
  /** Better Auth's own flag, set when a TOTP enrolment is verified. */
  readonly twoFactorEnabled: boolean;
}

/**
 * Read a Better Auth response, turning a refusal into an `Error` whose message
 * is the one Better Auth wrote.
 *
 * Better Auth's messages are in English and ours are in Danish. Where the
 * distinction matters to the person reading it — a wrong password, above all —
 * the caller passes `fallback` and gets Danish; Better Auth's own text is used
 * only when it says something we have no better wording for.
 */
async function readResponse<T>(
  response: Response,
  fallback: string,
): Promise<T> {
  const body = (await response.json().catch(() => null)) as
    | (T & { message?: string })
    | null;

  if (!response.ok) {
    throw new Error(
      response.status === 400 || response.status === 401 || response.status === 403
        ? fallback
        : (body?.message ?? fallback),
    );
  }
  return body as T;
}

async function post<T>(
  path: string,
  body: unknown,
  fallback: string,
): Promise<T> {
  const response = await fetch(`${API_BASE_URL}/api/auth/${path}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  return readResponse<T>(response, fallback);
}

/**
 * The signed-in user's email and 2FA state.
 *
 * The email is not in our own `users` table — Better Auth owns credentials —
 * so the page reads it from the session rather than from `GET /v1/users/me`.
 */
export async function getSessionUser(): Promise<SessionUser | null> {
  const response = await fetch(`${API_BASE_URL}/api/auth/get-session`, {
    credentials: 'include',
  });
  if (!response.ok) {
    return null;
  }
  const body = (await response.json().catch(() => null)) as {
    user?: { email?: string; name?: string; twoFactorEnabled?: boolean };
  } | null;
  const user = body?.user;
  if (user?.email === undefined) {
    return null;
  }
  return {
    email: user.email,
    name: user.name ?? '',
    twoFactorEnabled: user.twoFactorEnabled === true,
  };
}

export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  await post(
    'change-password',
    {
      currentPassword,
      newPassword,
      // Every other session is signed out. Changing a password is what a person
      // does after fearing someone else has it, and leaving those sessions
      // alive would defeat the act.
      revokeOtherSessions: true,
    },
    'Den nuværende adgangskode er forkert.',
  );
}

export interface TotpEnrolment {
  /** `otpauth://totp/...` — what the QR code encodes. */
  readonly totpURI: string;
  readonly backupCodes: readonly string[];
}

/**
 * Begin TOTP enrolment. **This does not turn 2FA on.**
 *
 * Better Auth sets `twoFactorEnabled` in `/two-factor/verify-totp`, not here —
 * `skipVerificationOnEnable` is not set in our config, deliberately. So the flow
 * is: enable (get the secret) → the person scans it → verify (prove they can
 * produce a code). Anything else can lock someone out of their own account with
 * an authenticator that never actually received the secret.
 */
export async function beginTwoFactor(password: string): Promise<TotpEnrolment> {
  return post<TotpEnrolment>(
    'two-factor/enable',
    { password },
    'Adgangskoden er forkert.',
  );
}

/**
 * Finish enrolment by proving the authenticator works. This is what turns 2FA
 * on — see `beginTwoFactor`. Also used at sign-in, from `client.ts`.
 */
export async function confirmTwoFactor(code: string): Promise<void> {
  await post('two-factor/verify-totp', { code }, 'Koden blev ikke godkendt.');
}

export async function disableTwoFactor(password: string): Promise<void> {
  await post('two-factor/disable', { password }, 'Adgangskoden er forkert.');
}
